export type Theme = 'light' | 'dark';
export type Density = 'comfortable' | 'compact';

const STORAGE_KEY = 'mvcp-theme';
const DENSITY_KEY = 'mvcp-density';

/** Resolve the initial theme: stored choice, else the OS preference. */
export function getInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch {
    // localStorage may be unavailable (private mode / SSR) — fall through.
  }
  const prefersDark =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'light';
}

/** Stamp the theme on the document root; token overrides key off [data-theme]. */
export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
}

export function persistTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Non-fatal: the theme still applies for the session.
  }
}

/**
 * Density mirrors theme: server-side is the per-user source of truth (Story 1.6),
 * localStorage is only a pre-paint cache to avoid a flash before /me/settings
 * resolves. Comfortable is the default.
 */
export function getInitialDensity(): Density {
  try {
    const stored = localStorage.getItem(DENSITY_KEY);
    if (stored === 'comfortable' || stored === 'compact') {
      return stored;
    }
  } catch {
    // localStorage may be unavailable — fall through to the default.
  }
  return 'comfortable';
}

/** Stamp the density on the document root; token overrides key off [data-density]. */
export function applyDensity(density: Density): void {
  document.documentElement.setAttribute('data-density', density);
}

export function persistDensity(density: Density): void {
  try {
    localStorage.setItem(DENSITY_KEY, density);
  } catch {
    // Non-fatal: density still applies for the session.
  }
}
